import { useId } from "react";
import styles from "./ThemeToggle.module.css";
import { THEME_PREFERENCES } from "./theme";
import type { ThemePreference } from "./theme";
import { useTheme } from "./useTheme";

const LABELS: Record<ThemePreference, string> = {
  system: "System",
  light: "Light",
  dark: "Dark",
};

/**
 * Native radios in a fieldset: arrow-key navigation, grouping and the checked state all
 * come from the platform, so keyboard and screen-reader behaviour is correct by default.
 */
function Glyph({ preference }: { preference: ThemePreference }) {
  const common = {
    className: styles.glyph,
    viewBox: "0 0 16 16",
    "aria-hidden": true,
    focusable: "false" as const,
  };
  if (preference === "light") {
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="3.2" fill="currentColor" />
        <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
          <path d="M8 1v1.8M8 13.2V15M15 8h-1.8M2.8 8H1M12.9 3.1l-1.3 1.3M4.4 11.6l-1.3 1.3M12.9 12.9l-1.3-1.3M4.4 4.4L3.1 3.1" />
        </g>
      </svg>
    );
  }
  if (preference === "dark") {
    return (
      <svg {...common}>
        <path
          d="M13.4 9.6A5.8 5.8 0 0 1 6.4 2.6 6 6 0 1 0 13.4 9.6Z"
          fill="currentColor"
        />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 2a6 6 0 0 1 0 12Z" fill="currentColor" />
    </svg>
  );
}

export function ThemeToggle() {
  const { preference, setPreference } = useTheme();
  const name = useId();

  return (
    <fieldset className={styles.group}>
      <legend className={styles.legend}>Colour theme</legend>
      {THEME_PREFERENCES.map((option) => {
        const id = `${name}-${option}`;
        return (
          <span className={styles.option} key={option}>
            <input
              className={styles.input}
              type="radio"
              id={id}
              name={name}
              value={option}
              checked={preference === option}
              onChange={() => {
                setPreference(option);
              }}
            />
            <label className={styles.label} htmlFor={id}>
              <Glyph preference={option} />
              {LABELS[option]}
            </label>
          </span>
        );
      })}
    </fieldset>
  );
}
